import { ShiftCard } from "./ShiftCard";
import styles from "./CleanerSchedule.module.css";

interface CheckInRecord {
  readonly recordedAt: string;
  readonly verification: string;
}

interface ShiftWithSite {
  readonly id: string;
  readonly date: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly siteName: string;
  readonly siteAddress: string | null;
  readonly sitePostcode: string | null;
  readonly siteNotes: string | null;
  readonly siteLatitude: number | null;
  readonly siteLongitude: number | null;
  readonly siteGpsRadiusMetres: number;
  readonly checkIn: CheckInRecord | null;
  readonly checkOut: CheckInRecord | null;
}

interface UpcomingShift {
  readonly id: string;
  readonly date: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly siteName: string;
  readonly siteAddress: string | null;
  readonly siteNotes: string | null;
}

interface CleanerScheduleProps {
  readonly todayShifts: readonly ShiftWithSite[];
  readonly upcomingShifts: readonly UpcomingShift[];
  readonly todayDate: string;
}

function formatTimeFriendly(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const suffix = h >= 12 ? "pm" : "am";
  const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hour}:${String(m).padStart(2, "0")}${suffix}`;
}

function formatDayLabel(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const todayStr = today.toISOString().split("T")[0];
  const tomorrowStr = tomorrow.toISOString().split("T")[0];

  if (dateStr === todayStr) return "Today";
  if (dateStr === tomorrowStr) return "Tomorrow";

  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

function formatTodayHeading(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

export function CleanerSchedule({
  todayShifts,
  upcomingShifts,
  todayDate,
}: CleanerScheduleProps) {
  return (
    <div className={styles.container}>
      <div className={styles.todaySection}>
        <div className={styles.sectionTitle}>
          Today, {formatTodayHeading(todayDate)}
        </div>

        {todayShifts.length === 0 ? (
          <div className={styles.emptyToday}>
            You're all done for today!
          </div>
        ) : (
          todayShifts.map((shift, index) => (
            <ShiftCard
              key={shift.id}
              id={shift.id}
              siteName={shift.siteName}
              siteAddress={shift.siteAddress}
              sitePostcode={shift.sitePostcode}
              siteLatitude={shift.siteLatitude}
              siteLongitude={shift.siteLongitude}
              startTime={shift.startTime}
              endTime={shift.endTime}
              siteNotes={shift.siteNotes}
              checkIn={shift.checkIn}
              checkOut={shift.checkOut}
              staggerIndex={index}
            />
          ))
        )}
      </div>

      <div>
        <div className={styles.sectionTitle}>Coming up</div>

        {upcomingShifts.length === 0 ? (
          <div className={styles.emptyUpcoming}>
            Nothing on the horizon yet.
          </div>
        ) : (
          <div className={styles.upcomingList}>
            {upcomingShifts.map((shift) => (
              <div key={shift.id} className={styles.upcomingItem}>
                <span className={styles.upcomingDay}>{formatDayLabel(shift.date)}</span>
                <span className={styles.upcomingSite}>{shift.siteName}</span>
                <span className={styles.upcomingTime}>
                  {formatTimeFriendly(shift.startTime)} - {formatTimeFriendly(shift.endTime)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
